import { Geo } from '@wellesley/openapi'
import { toast } from '@wellesley/system-toast'
import { logger } from '../logger'
import { localStorage } from 'helpers/localStorage'
type GetGeo = {
  onSuccess: (position: GeolocationPosition) => void
  onError: (err: GeolocationPositionError) => void
}

type StoredGeo = {
  [key: string]: {
    tz: string
    geo: Geo
    date: Date
  } | null
}

const storageKey = `geo`
const storage = localStorage('user')

export const isSupported = () => 'geolocation' in navigator

export const getLocation = ({ onSuccess, onError }: GetGeo) => {
  if (!isSupported()) {
    toast.error('Geolocation is not supported in your browser')
    return null
  }

  return navigator.geolocation.getCurrentPosition(onSuccess, onError)
}

export const getLocationPermissionState =
  async (): Promise<PermissionState | null> => {
    if (!navigator.permissions?.query) return null

    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'geolocation',
      })
      return permissionStatus.state
    } catch {
      return null
    }
  }

export const getStoredGeo = () => {
  try {
    const storedData = storage.getItem(storageKey)
    const storedGeo = storedData ? (JSON.parse(storedData) as StoredGeo) : null

    return storedGeo
  } catch (e) {
    toast.error('Can not get cached geo from localstorage')
    return null
  }
}

export const getStoredGeoFor = (userId?: string) => {
  if (!userId) {
    logger.debug(`${storageKey}: can not get stored geo, userId is missing`)
    return null
  }

  const fullGeo = getStoredGeo()

  return fullGeo?.[userId]
}

const isSameDayAsToday = (dateISO?: Date) => {
  if (!dateISO) return false

  const today = new Date()
  const date = new Date(dateISO)

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  )
}

const updateGeo = (userId: string, data: Partial<StoredGeo[string]>) => {
  const existingData = getStoredGeo()

  storage.setItem(
    storageKey,
    JSON.stringify({
      ...existingData,
      [userId]: {
        ...existingData?.[userId],
        ...data,
        date: isSameDayAsToday() ? existingData?.[userId]?.date : new Date(),
      },
    }),
  )
}

export const saveGeoToStore = (geo: Geo, userId?: string) => {
  if (!userId) {
    return logger.debug(
      `${storageKey}: can not save geo to store, userId is missing`,
    )
  }

  updateGeo(userId, { geo })
}

export const saveTimezoneToStore = (tz: string, userId?: string) => {
  if (!userId) {
    return logger.debug(
      `${storageKey}: can not save tz to store, userId is missing`,
    )
  }

  updateGeo(userId, { tz })
}

type GetGeoProps = {
  cb: (geo: Geo) => void | Promise<void>
  togglePending: (pending: boolean) => void
}

export const getGeo = ({ cb, togglePending }: GetGeoProps) => {
  togglePending(true)
  getLocation({
    onSuccess: (position) => {
      void cb(position.coords)
    },
    onError: (e) => {
      togglePending(false)
      const message =
        e.code === 1
          ? 'Location access denied. Enable it for automatic detection'
          : `Update location error: ${e.message}`
      toast.error(message)
    },
  })
}
